/* eslint-disable max-len */
import { JANZU_URL } from "config/config";
import { BINARY_CLASSIFICATION, BOUNDING_BOXES } from "config/constants";
import { UNSURE_LABEL, labelMappings } from "config/LabelMapping";
import { StepsDataProps, ValidBoxLabelsProps, createArrayRoisType } from "types/boundingBoxesContextTypes";
import { DetectorModes } from "types/createDetector";
import { Detector } from "types/detectors";
import { Disagreement } from "types/disagreements";
import { CanvasAnnotation } from "types/imageAnnotations";
import { ImageQuery, Posicheck } from "types/imageQueries";

export const DEFAULT_BOUNDING_BOX_COLOR = "#00C7D9";

export interface PresentedInfoToLabel {
  current_image_query: string;
  query: string;
  bounding_boxes: string;
  yesExamples: { examples: string[] };
  NoExamples: { examples: string[] };
}

const LABEL_COLORS: { [key: string]: string } = {
  YES: "hl-purple",
  NO: "hl-magenta",
  DEFAULT: "hl-default",
};

export const getLabelColor = (label: string) => LABEL_COLORS[label] || LABEL_COLORS.DEFAULT;

export function isGroundTruthPool(detectorId: string | null, isFlagged?: boolean, isGroundTruth?: boolean): boolean {
  return Boolean(isGroundTruth && !!detectorId && !isFlagged);
}

export function constructPollImageQueryUrl(
  detectorId: string | null,
  groupId: string | null,
  unclearMode?: boolean,
  isFlagged?: boolean,
  isGroundTruth?: boolean
) {
  const queryParams = [];

  if (isGroundTruth) return `${JANZU_URL}reef-api/image-queries/ground-truth-pool?predictor_id=${detectorId}`;

  if (unclearMode || isFlagged) {
    queryParams.push(`unclear_only=${unclearMode || isFlagged}`);
  }

  if (detectorId) {
    queryParams.push(`predictor_id=${detectorId}`);
  }

  if (groupId) {
    queryParams.push(`predictor_set_id=${groupId}`);
  }

  const queryString = `?${queryParams.join("&").trim()}`;

  return `${JANZU_URL}reef-api/review-pool${queryString.length > 1 ? queryString : ""}`;
}

// check if the current predictor is bounding boxes or not
export const checkIsBoundingBoxes = (currentPredictor: Detector | undefined) => {
  if (!currentPredictor) {
    return false;
  }
  const validBoxes = currentPredictor.valid_bbox_labels !== null ? currentPredictor.valid_bbox_labels || [] : [];
  return currentPredictor.labeling_task === "BOUNDING BOXES" && validBoxes.length !== 0;
};

export const checkIsGroundlightBoundingBoxes = (detector: Detector | undefined) => {
  if (!detector) {
    return false;
  }
  if (detector.mode === DetectorModes.COUNT || detector.mode === DetectorModes.BOUNDING_BOX) {
    return true;
  }
  // There are some old Binary detectors that have the capability to do bounding boxes
  if (detector.mode === DetectorModes.BINARY) {
    return checkIsBoundingBoxes(detector);
  }
  return false;
};

// This functions returns an array of image query ids extracted from an array of image queries
export const extractIds = (imageQueries: ImageQuery[]) =>
  imageQueries.map((imageQuery: ImageQuery) => imageQuery.iq_id);

export const unclearOptionsArr = [
  { name: "Poor image quality (e.g. blurry, dark)", value: "poor image quality" },
  { name: "Need more information (Query / Notes unclear)", value: "need more information" },
  { name: "Image does not relate to query", value: "image does not relate to query" },
  { name: "Important area of image obscured", value: "important area of image obscured" },
];

export const contructViewWhenLabeling = (
  currentPosicheck: Posicheck,
  yesExamples: ImageQuery[],
  noExamples: ImageQuery[]
) => ({
  current_image_query: currentPosicheck?.id,
  query: currentPosicheck?.predictor.label_prompt,
  bounding_boxes: checkIsBoundingBoxes(currentPosicheck?.predictor) ? BOUNDING_BOXES : BINARY_CLASSIFICATION,
  yesExamples: {
    examples: extractIds(yesExamples),
  },
  NoExamples: {
    examples: extractIds(noExamples),
  },
});

export function getBackendLabel(renderedLabel: string) {
  const searchLabel = renderedLabel.toLowerCase(); // Convert input to lower case
  for (let i = 0; i < labelMappings.length; i += 1) {
    if (labelMappings[i].renderedLabel.toLowerCase() === searchLabel) {
      return labelMappings[i].backendLabel;
    }
  }
  return !renderedLabel ? UNSURE_LABEL : renderedLabel; // or any default value you prefer if no match is found
}

/**
 * General function to map annotations to validBoxLabels with customizable behavior.
 *
 * @param {ROIAnnotation[]} annotations - Array of ROIAnnotation objects.
 * @param {ValidBoxLabelsProps[]} validBoxLabels - Array of ValidBoxLabelsProps objects.
 * @param {StepsDataProps} stepsData - StepsDataProps object containing the current step information.
 * @param {(item: ValidBoxLabelsProps, annotations: ROIAnnotation[]) => ValidBoxLabelsProps} handleMatchingLabel - Callback function that defines custom behavior for items matching the current label.
 * @returns {ValidBoxLabelsProps[]} The updated validBoxLabels array with annotations mapped as per the custom behavior defined in handleMatchingLabel.
 */
export const mapAnnotations = (
  annotations: CanvasAnnotation[],
  validBoxLabels: ValidBoxLabelsProps[],
  stepsData: StepsDataProps,
  handleMatchingLabel: (item: ValidBoxLabelsProps, annotations: CanvasAnnotation[]) => ValidBoxLabelsProps
) => {
  const { currentStep } = stepsData;
  const currentLabel = validBoxLabels[currentStep - 1]?.label || "";
  return validBoxLabels.map((item) => {
    // Apply custom behavior for items matching the current label.
    if (item.label === currentLabel) {
      return handleMatchingLabel(item, annotations);
    }
    // Filter annotations by color and exclude duplicates.
    const roisPerColor = annotations.filter(
      (roi) => roi.strokeColor === item.color && !item.items.some((existingItem) => existingItem.id === roi.id)
    );
    // Return the updated item with combined annotations.
    return {
      ...item,
      items: item.items ? [...item.items, ...roisPerColor] : [...roisPerColor],
    };
  });
};

// mapLabels: Maps annotations to validBoxLabels and updates items with all annotations for the current label.
export const mapLabels = (
  annotations: CanvasAnnotation[],
  validBoxLabels: ValidBoxLabelsProps[],
  stepsData: StepsDataProps
) =>
  mapAnnotations(annotations, validBoxLabels, stepsData, (item, annotations) => ({
    ...item,
    // Update items with all annotations for the current label.
    items: [...annotations],
  }));

// mapDeleteLabels: Similar to mapLabels, but sets items to an empty array for the current label.
export const mapDeleteLabels = (
  annotations: CanvasAnnotation[],
  validBoxLabels: ValidBoxLabelsProps[],
  stepsData: StepsDataProps
) =>
  mapAnnotations(annotations, validBoxLabels, stepsData, (item) => ({
    ...item,
    // Set items to an empty array for the current label.
    items: [],
  }));

// create a array with data rois format for the endpoint
export const createArrayRois: createArrayRoisType = (data: ValidBoxLabelsProps[]) => {
  if (data.length === 0) {
    return [];
  }

  const annotations = data.flatMap((item) => item.items);
  return annotations;
};

export const removeDuplicates = (
  firstArray: (Posicheck | Disagreement)[],
  secondArray: (Posicheck | Disagreement)[]
) => {
  const idsInFirstArray = new Set(firstArray.map((obj) => obj.id));
  const filterResults = secondArray.length > 0 ? secondArray.filter((obj) => !idsInFirstArray.has(obj.id)) : [];
  return [...firstArray, ...filterResults];
};

export const filterRecentlyAddedIQs = (imageQueries: (Posicheck | Disagreement)[], labelIdsHistory: Set<string>) =>
  imageQueries.filter((iq) => !labelIdsHistory.has(iq.id));
